<script setup>
import { ref, onMounted, onUnmounted, watch, computed } from "vue";
import {
  PencilSquareIcon,
  TrashIcon,
  CheckCircleIcon,
} from "@heroicons/vue/24/solid";
import { getClients, deleteClient } from "@/services/clients";
import EditClientForm from "./EditClientForm.vue";
import { Toast } from "bootstrap";
import ModalDelete from "../ModalDelete.vue";

const showEditClientForm = ref(false);
const isDropdownOpen = ref(false);
const offcanvasEditClientFormRef = ref(null);

const selectedClient = ref(null);

const clients = ref([]);
const showSuccessMessage = ref(false);
const isClientEdited = ref(false);

const deletedClientName = ref("");
const showDeleteModal = ref(false);
const clientToDelete = ref(null);
const deleteToast = ref(null);

const clientsPerPage = 10;
const currentPage = ref(1);
const paginatedClients = computed(() => {
  const start = (currentPage.value - 1) * clientsPerPage;
  return clients.value.slice(start, start + clientsPerPage);
});
const totalPages = computed(() => {
  return Math.ceil(clients.value.length / clientsPerPage) || 1;
});
const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
};
const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};
const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

onMounted(async () => {
  const clientsResponse = await getClients();
  console.log("Fetched clients:", clientsResponse);
  if (clientsResponse.data.length) {
    clients.value = clientsResponse.data;
  }
});

onMounted(() => {
  deleteToast.value.addEventListener("shown.bs.toast", () => {
    setTimeout(() => {
      deleteToast.value.classList.remove("show");
    }, 3000);
  });
});

const promptDeleteClient = (client) => {
  clientToDelete.value = client;
  showDeleteModal.value = true;
};

const confirmDeleteClient = async () => {
  if (!clientToDelete.value) return;
  try {
    await deleteClient(clientToDelete.value.clientsId);
    deletedClientName.value = clientToDelete.value.companyName;
    clients.value = clients.value.filter(
      (c) => c.clientsId !== clientToDelete.value.clientsId
    );

    const bsToast = new Toast(deleteToast.value);
    bsToast.show();
  } catch (error) {
    console.error("Error al eliminar cliente:", error);
  } finally {
    showDeleteModal.value = false;
    clientToDelete.value = null;
  }
};

const cancelDeleteClient = () => {
  showDeleteModal.value = false;
  clientToDelete.value = null;
};

function toggleEditClientForm(client) {
  selectedClient.value = client;
  console.log("Selected Client:", selectedClient.value);
  showEditClientForm.value = !showEditClientForm.value;
  showSuccessMessage.value = false;
}
/* Click fuera del dropdown */
onMounted(() => {
  document.addEventListener("click", (event) => {
    if (!event.target.closest(".dropdown")) {
      isDropdownOpen.value = false;
    }
  });
});

const closeOffcanvas = () => {
  showEditClientForm.value = false;
  if (!isClientEdited.value) {
    showSuccessMessage.value = false;
  }
};
const handleKeySaved = async (newData) => {
  const updatedClientIndex = clients.value.findIndex(
    (client) => client.id === newData.id
  );
  if (updatedClientIndex !== -1) {
    clients.value[updatedClientIndex] = newData;
  } else {
    clients.value.push(newData);
  }

  showEditClientForm.value = false;
  isClientEdited.value = true;
  showSuccessMessage.value = true;

  try {
    const clientsResponse = await getClients();
    if (clientsResponse.data.length) {
      clients.value = clientsResponse.data;
    }
  } catch (error) {
    console.error("Error al recargar los clientes:", error);
  }
};

const handleEscape = (event) => {
  if (event.key === "Escape") {
    closeOffcanvas();
  }
};
onMounted(() => {
  window.addEventListener("keydown", handleEscape);
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleEscape);
});

watch(showEditClientForm, (newValue) => {
  if (!newValue) {
    setTimeout(() => {
      const offcanvasElement = document.querySelector(
        "#offcanvasEditClientForm"
      );
      offcanvasElement.classList.add("show");
    }, 100);
  }
});
</script>
<template>
  <div class="rounded-lg table-container" style="width: 100%">
    <table class="table" style="width: 100%">
      <thead>
        <tr>
          <th>Nombre de la Empresa</th>
          <th>Nombre de Contacto</th>
          <th>Teléfono de Contacto</th>
          <th>Email de Contacto</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="client in paginatedClients" :key="client.id">
          <td style="text-transform: capitalize">{{ client.companyName }}</td>
          <td style="text-transform: capitalize">{{ client.contactName }}</td>
          <td>{{ client.phone }}</td>
          <td>{{ client.email }}</td>
          <td
            class="d-flex gap-3 actions-column mx-2 justify-content-around"
            style="margin-top: 3px; border-bottom: none"
          >
            <button
              class="buttons-group"
              @click="toggleEditClientForm(client)"
              data-bs-toggle="offcanvas"
              data-bs-target="#offcanvasEditClientForm"
              aria-controls="offcanvasEditClientForm"
            >
              <PencilSquareIcon
                class="text-blue-500 d-flex justify-content-center"
                style="color: #517fa4; width: 23px; height: 23px"
              ></PencilSquareIcon>
            </button>
            <button
              class="buttons-group delete"
              @click="promptDeleteClient(client)"
            >
              <TrashIcon
                class="text-blue-500 d-flex justify-content-center"
                style="width: 23px; height: 23px"
              ></TrashIcon>
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <!-- PAGINACIÓN -->
    <div class="pagination-container d-flex flex-column align-items-end mt-3">
      <div class="pagination-controls d-flex align-items-center gap-2 justify-content-end">
        <button class="pagination-btn" :disabled="currentPage === 1" @click="prevPage">&lt;</button>
        <button
          v-for="page in totalPages"
          :key="page"
          class="pagination-btn"
          :class="{ active: page === currentPage }"
          @click="goToPage(page)"
        >
          {{ page }}
        </button>
        <button class="pagination-btn" :disabled="currentPage === totalPages" @click="nextPage">&gt;</button>
      </div>
      <div class="pagination-info mt-2 text-end">
        Mostrando
        <b>{{ (currentPage - 1) * clientsPerPage + 1 }}</b>
        -
        <b>{{ Math.min(currentPage * clientsPerPage, clients.length) }}</b>
        de
        <b>{{ clients.length }}</b>
        resultados
      </div>
    </div>
    <!-- FIN PAGINACIÓN -->
    <div
      class="toast align-items-center position-fixed top-50 end-0 p-2 w-auto"
      style="background: #243949; color: white; width: auto"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      id="deleteToast"
      ref="deleteToast"
    >
      <div class="d-flex">
        <div class="toast-body">
          Se ha eliminado el cliente
          <span
            style="
              font-weight: 600;
              text-decoration: underline;
              text-transform: capitalize;
            "
          >
            {{ deletedClientName }}
          </span>
          con éxito.
        </div>
        <button
          type="button"
          class="btn-close me-2 m-auto"
          data-bs-dismiss="toast"
          aria-label="Close"
        ></button>
      </div>
    </div>
  </div>
  <div
    class="offcanvas offcanvas-end"
    style="width: 45%"
    data-bs-scroll="true"
    tabindex="-1"
    id="offcanvasEditClientForm"
    aria-labelledby="offcanvasEditClientFormLabel"
    ref="offcanvasEditClientFormRef"
  >
    <div class="offcanvas-header">
      <button
        type="button"
        class="btn-close"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
        @click="closeOffcanvas"
      ></button>
    </div>
    <div class="offcanvas-body" v-if="showEditClientForm">
      <EditClientForm
        :offcanvasRef="offcanvasEditClientFormRef"
        :newKeyData="selectedClient"
        @keySaved="handleKeySaved"
        @cancelEdit="closeOffcanvas"
      ></EditClientForm>
    </div>
    <!-- *Mensaje -->
    <div
      v-if="showSuccessMessage"
      class="offcanvas-body"
      style="text-align: center; margin-top: 8rem"
    >
      <CheckCircleIcon
        style="width: 5rem; height: 5rem; color: #517fa4"
      ></CheckCircleIcon>
      <h4 class="create-title mt-3">El Cliente se editó con éxito.</h4>
      <button
        class="globals-border-button"
        data-bs-dismiss="offcanvas"
        style="margin-top: 3rem"
      >
        Salir
      </button>
    </div>
  </div>

  <ModalDelete
    :show="showDeleteModal"
    title="Confirmar Eliminación"
    :message="`¿Estás seguro de que deseas eliminar el cliente <strong>${clientToDelete?.companyName}</strong>?`"
    @confirm="confirmDeleteClient"
    @cancel="cancelDeleteClient"
  />
</template>
<style lang="scss" scoped>
@import "@/_scss/global/_variables.scss";
.actions-column {
  position: sticky;
  right: 0;
  margin-left: 4px;
  margin-right: 4px;
}
.buttons-group {
  border: none;
  background: transparent;
  cursor: pointer;
}
.delete:hover {
  background-color: $gradient-color-01;
}
.rounded-lg {
  border-radius: 10px;
  overflow: hidden;
}
.table {
  width: 100%;
  border-collapse: collapse;
}

th,
td {
  padding: 6px 8px 6px 8px;
  margin-top: 3px;
  margin: 4px;
  text-align: start;
  border-bottom: 1px solid $seven-color;
  white-space: nowrap;
}

th {
  background-color: $seven-color;
  color: $primary-color;
  text-align: start;
  padding-top: 10px;
  padding-bottom: 10px;
  margin: 5px 2px 5px 2px;
}

.under-th {
  height: 10px;
  font-size: x-small;
  padding: 2px;
}

.table-container {
  width: 100%;
  max-width: none;
  overflow-x: auto;
  overflow-y: hidden;
}

.table-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
  border-radius: 10px;
  background-color: $white-color;
}

.table-container::-webkit-scrollbar-thumb {
  background-color: $seven-color;
  border-radius: 10px;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background-color: $six-color;
}

.pagination-container {
  width: 100%;
  margin-top: 1.5rem;
  align-items: flex-end;
}
.pagination-controls {
  gap: 0.5rem;
}
.pagination-btn {
  border: 1.5px solid #355E77;
  background: #fff;
  color: #355E77;
  border-radius: 50%;
  width: 2.2rem;
  height: 2.2rem;
  font-weight: 700;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.18s, color 0.18s, border 0.18s;
  cursor: pointer;
  margin: 0 2px;
}
.pagination-btn.active, .pagination-btn:hover {
  background: #355E77;
  color: #fff;
  border: 1.5px solid #355E77;
}
.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.pagination-info {
  font-size: 0.98rem;
  color: #355E77;
  margin-top: 0.5rem;
}
</style>
